import { BodyText, Button, Flex } from '@utilitywarehouse/hearth-react';
import { ReactElement, useEffect } from 'react';

import { Dialog, DialogActions, DialogID, useDialog } from '@components/Dialog';
import {
  IOTSCommunication,
  IOTSProvider,
  IOTSSwitchOption,
  OTSServiceAction,
} from '@src/types/oneTouchSwitching';

import { commsOptionSplitterHandler } from '../../commsOptionsSplit';
import { FoundHomePhone } from './FoundHomePhone';
import './styles.scss';

interface FoundDialogProps {
  open: boolean;
  onContinue: () => void;
  provider: IOTSProvider | null;
  comms: IOTSCommunication[];
  homePhoneSuffix: string | null;
  switchOptions: IOTSSwitchOption[];
  switchingReference: string | null;
  setReference: (ref: string) => void;
  canUpdateState: boolean;
}

interface FoundDialogUIProps {
  dialogId: DialogID;
  onContinue: () => void;
  services: (string | null)[];
  provider: IOTSProvider | null;
  commOptions: ReactElement[] | null;
  homePhoneSuffix: string | null;
  switchOptions: IOTSSwitchOption[];
  switchingReference: string | null;
  setReference: (ref: string) => void;
  canUpdateState: boolean;
}

interface FoundDialogHeaderProps {
  services: (string | null)[];
  provider: IOTSProvider | null;
}

interface FoundDialogContentProps {
  provider: IOTSProvider | null;
  homePhoneSuffix: string | null;
  switchOptions: IOTSSwitchOption[];
  switchingReference: string | null;
  setReference: (ref: string) => void;
  canUpdateState: boolean;
  commOptions?: ReactElement[] | null;
}

const FoundDialogContent = ({
  provider,
  commOptions,
  homePhoneSuffix,
  setReference,
  switchingReference,
  switchOptions,
  canUpdateState,
}: FoundDialogContentProps) => (
  <Flex direction="column" gap="200" alignItems="center" marginY="200">
    <BodyText>
      {provider?.name} have sent you {commOptions} with details of what will
      happen when you switch.
    </BodyText>
    {homePhoneSuffix && provider && (
      <FoundHomePhone
        provider={provider}
        homePhoneSuffix={homePhoneSuffix}
        setReference={setReference}
        switchingReference={switchingReference}
        switchOptions={switchOptions}
        canUpdateState={canUpdateState}
      />
    )}
  </Flex>
);

const FoundDialogUI = ({
  dialogId,
  onContinue,
  services,
  provider,
  commOptions,
  homePhoneSuffix,
  switchOptions,
  switchingReference,
  setReference,
  canUpdateState,
}: FoundDialogUIProps) => {
  const noServices = services.length === 0;
  return (
    <Dialog
      id={dialogId}
      heading={
        noServices
          ? `We've found your service with ${provider?.name}`
          : `We've found your ${services.join(' and ')} with ${provider?.name}`
      }
      image={<img src="oneTouchSwitching/tick.svg" height={48} width={48} />}
      hideCloseButton
      preventDismiss
    >
      <FoundDialogContent
        provider={provider}
        commOptions={commOptions}
        homePhoneSuffix={homePhoneSuffix}
        setReference={setReference}
        switchingReference={switchingReference}
        switchOptions={switchOptions}
        canUpdateState={canUpdateState}
      />
      <DialogActions>
        <Button onClick={onContinue} variant="solid">
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FoundDialog: React.FC<FoundDialogProps> = ({
  open,
  onContinue,
  provider,
  comms,
  homePhoneSuffix,
  switchOptions,
  switchingReference,
  setReference,
  canUpdateState,
}) => {
  const { setOpen, id: dialogId } = useDialog(DialogID.FOUND_OTS);
  const commOptions = commsOptionSplitterHandler(comms);

  const hasBroadband =
    switchOptions.findIndex((o) =>
      [OTSServiceAction.Cease, OTSServiceAction.ForcedCease].includes(
        o.broadbandAction
      )
    ) !== -1;
  const services = [
    hasBroadband ? 'broadband' : null,
    homePhoneSuffix ? 'home phone' : null,
  ].filter((v) => !!v);

  useEffect(() => {
    if (canUpdateState && open) {
      setOpen(true);
    } else if (canUpdateState) {
      setOpen(false);
    }
  }, [canUpdateState, open, setOpen]);

  return (
    <FoundDialogUI
      dialogId={dialogId}
      onContinue={onContinue}
      services={services}
      provider={provider}
      commOptions={commOptions}
      homePhoneSuffix={homePhoneSuffix}
      switchOptions={switchOptions}
      switchingReference={switchingReference}
      setReference={setReference}
      canUpdateState={canUpdateState}
    />
  );
};

export type {
  FoundDialogProps,
  FoundDialogUIProps,
  FoundDialogHeaderProps,
  FoundDialogContentProps,
};

export { FoundDialog, FoundDialogUI };
